import React, { useState, useEffect } from "react";
import axios from 'axios'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Box,
  Typography,
  CircularProgress
} from "@mui/material";
import { smallNumberFilterPercent } from "../../../../../../helperFunctions/smallNumberFilter";

const apiUrl = process.env.REACT_APP_API_URL;

// Define the data type
interface DataRow {
  employeeName: string;
  percentTimeInMaint: number;
}

interface TableThreeProps {
  highlighting: boolean;
  dateRange: [Date | undefined, Date | undefined];
  currentArea: string;
  currentSubArea: string | null;
  filterSmallValues: boolean
}

// Function to return color based on percentageTimeInMaint
const getColorForColumn = (column: keyof DataRow, value: number): string => {
  switch (column) {
    case "percentTimeInMaint":
      if (value >= 20) return "#0c5720";   // Dark green for the highest values (20%+)
      if (value >= 15) return "#47a02f";   // Strong green (15-19%)
      if (value >= 10) return "#75d62b";   // Medium green (10-14%)
      if (value >= 8)  return "#99c93e";   // Light green (8-9%)
      if (value >= 6)  return "#b0c93e";   // Green-yellow (6-7%)
      if (value >= 4)  return "#cbd934";   // Yellow (4-5%)
      if (value >= 2)  return "#d99134";   // Orange (2-3%)
      if (value >= 1)  return "#d96b34";   // Dark orange (1-1.9%)
      if (value >= 0.5) return "#d94434";  // Tomato (0.5-0.9%)
      return "#f2f2f2"; // Default light gray

    default:
      return "#f2f2f2"; // Default light gray
  }
};

type Order = "asc" | "desc";

// Sorting helpers
const descendingComparator = <T,>(a: T, b: T, orderBy: keyof T): number => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = <T,>(
  order: Order,
  orderBy: keyof T
): ((a: T, b: T) => number) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const stableSort = <T,>(
  array: T[],
  comparator: (a: T, b: T) => number
): T[] => {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

function TableThree({
  highlighting,
  dateRange,
  currentArea,
  currentSubArea,
  filterSmallValues
}: TableThreeProps) {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof DataRow>("employeeName");
  const [data, setData] = useState<DataRow[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);


  useEffect(() => {
    if (!dateRange[0] || !dateRange[1]) {
      return
    }

    const fetchData = async () => {
      try {
        setLoading(true)
        let response = await axios.get(`${apiUrl}/api/employees-maintenance-time`, 
          {
          params: {
            startDate: dateRange[0]?.toISOString(),  
            endDate: dateRange[1]?.toISOString(),
            currentArea: currentArea    
          }
        })
        // console.log('test',response.data)
        setData(response.data)
        
      } catch (error) {
        // console.log("There was an error fetching Maint Time by Emp data")
      } finally {
        setLoading(false)
      }
    }
    fetchData()
    },[dateRange,currentArea,currentSubArea])


  const handleRequestSort = (property: keyof DataRow) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = data ? stableSort(data, getComparator(order, orderBy)) : [];

  return (
    <>
      <TableContainer component={Paper} style={{ maxHeight: 185, overflow: "auto" }}>
        <Typography
          sx={{
            fontWeight: "bold",
            paddingTop: "1%",
            fontSize: "15px",
            marginLeft: "2%",
          }}
        >
          Employees {currentArea}
        </Typography>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "20px" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  { id: "employeeName", label: "Name" },
                  { id: "percentageTimeInMaint", label: "% of time in Maint" },
                ].map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align="right"
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                      padding: "2px 8px",
                      overflow: "hidden",
                      fontWeight: "bold",
                      textOverflow: "ellipsis",
                      zIndex: "0",
                      color: "black", // White text for contrast
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : "asc"}
                      onClick={() => handleRequestSort(headCell.id as keyof DataRow)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData
              .filter(row => !filterSmallValues || !smallNumberFilterPercent(row.percentTimeInMaint))
              .map((row, index) => (
                <TableRow key={index} sx={{ height: 24 }}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      padding: "2px 8px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontWeight: "bold",
                    }}
                  >
                    {row.employeeName}
                  </TableCell>
                  <TableCell
                    align="right"
                    style={{
                      backgroundColor: highlighting
                        ? getColorForColumn("percentTimeInMaint", row.percentTimeInMaint)
                        : undefined,
                      padding: "2px 8px",
                      fontWeight: "bold",
                    }}
                  >
                    {row.percentTimeInMaint}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </>
  );
}

export default TableThree;
